import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.js';
import PrivateRoute from './components/common/PrivateRoute.js';

// Auth Pages
import Login from './components/auth/Login.js';
import ForgotPassword from './components/auth/ForgotPassword.js';
import ResetPassword from './components/auth/ResetPassword.js';

import LandingPage from './components/home/LandingPage.js';
import AboutUs from './components/home/AboutUs.js';
import Services from './components/home/Services.js';
import ContactUs from './components/home/ContactUs.js';
import Features from './components/home/Features.js';
import IMOTankContainerCertification from "./components/home/services/IMOTankContainerCertification.js"
import ConditionSurveyImportExport from "./components/home/services/ConditionSurveyImportExport.js"
import OnOffHireSurvey from "./components/home/services/OnOffHireSurvey.js"
import PrePostRepairSurvey from "./components/home/services/PrePostRepairSurvey.js"
import TankInspectionReport from "./components/home/services/TankInspectionReport.js"

// Dashboard Pages
import AdminDashboard from './components/dashboard/AdminDashboard.js';
import DocumentationManagerDashboard from './components/dashboard/DocumentationManagerDashboard.js';
import SurveyInspectorDashboard from './components/dashboard/SurveyInspectorDashboard.js';
import ClientDashboard from './components/dashboard/ClientDashboard.js';
import AccountantDashboard from './components/dashboard/AccountantDashboard.js';

// User Pages
import UserList from './components/user/UserList.js';
import AddUser from './components/user/AddUser.js';
import EditUser from './components/user/EditUser.js';
import ViewUser from './components/user/ViewUser.js';

// Tank Pages
import TankList from './components/tank/TankList.js';
import AddTank from './components/tank/AddTank.js';
import EditTank from './components/tank/EditTank.js';
import ViewTank from './components/tank/ViewTank.js';

// Container Pages
import ContainerList from './components/containers/ContainerList';
import AddContainer from './components/containers/AddContainer';
import EditContainer from './components/containers/EditContainer';
import ViewContainer from './components/containers/ViewContainer';

// Role Pages
import RoleList from './components/role/RoleList.js';
import AddRole from './components/role/AddRole.js';
import EditRole from './components/role/EditRole.js';
import ViewRole from './components/role/ViewRole.js';

// Contact Pages
import ContactList from './components/contact/ContactList.js';
import EditContact from './components/contact/EditContact.js';
import ViewContact from './components/contact/ViewContact.js';

// Testimonial Pages
import TestimonialList from './components/testimonial/TestimonialList.js';
import AddTestimonial from './components/testimonial/AddTestimonial.js';
import EditTestimonial from './components/testimonial/EditTestimonial.js';
import ViewTestimonial from './components/testimonial/ViewTestimonial.js';

// Tank Cleaning Pages
import TankCleaningList from './components/tank-cleaning/TankCleaningList.js';
import AddTankCleaning from './components/tank-cleaning/AddTankCleaning.js';
import EditTankCleaning from './components/tank-cleaning/EditTankCleaning.js';
import ViewTankCleaning from './components/tank-cleaning/ViewTankCleaning.js';
import TankCleaningFileCabinet from './components/tank-cleaning/TankCleaningFileCabinet.js';

// Dry Box Survey Pages
import DryBoxSurveyList from './components/dry-box-survey/DryBoxSurveyList.js';
import AddDryBoxSurvey from './components/dry-box-survey/AddDryBoxSurvey.js';
import EditDryBoxSurvey from './components/dry-box-survey/EditDryBoxSurvey.js';
import ViewDryBoxSurvey from './components/dry-box-survey/ViewDryBoxSurvey.js';
import DBSFileCabinet from './components/dry-box-survey/DBSFileCabinet.js';

// TI Report Pages
import TIReportList from './components/ti-report/TIReportList.js';
import AddTIReport from './components/ti-report/AddTIReport.js';
import EditTIReport from './components/ti-report/EditTIReport.js';
import ViewTIReport from './components/ti-report/ViewTIReport.js';
import TIReportFileCabinet from './components/ti-report/TIReportFileCabinet.js';

// Location Pages
import LocationList from './components/location/LocationList.js';
import AddLocation from './components/location/AddLocation.js';
import EditLocation from './components/location/EditLocation.js';
import ViewLocation from './components/location/ViewLocation.js';

// OnHire Pages
import OnHireList from './components/onhire/OnHireList.js';
import AddOnHire from './components/onhire/AddOnHire.js';
import EditOnHire from './components/onhire/EditOnHire.js';
import ViewOnHire from './components/onhire/ViewOnHire.js';

// EI Receipt Pages
import EIReceiptList from './components/ei-receipt/EIReceiptList.js';
import AddEIReceipt from './components/ei-receipt/AddEIReceipt.js';
import EditEIReceipt from './components/ei-receipt/EditEIReceipt.js';
import ViewEIReceipt from './components/ei-receipt/ViewEIReceipt.js';

// Billing Pages
import BillingList from './components/billing/BillingList.js';
import AddBilling from './components/billing/AddBilling.js';
import EditBilling from './components/billing/EditBilling.js';
import ViewBilling from './components/billing/ViewBilling.js';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/services" element={<Services />} />
          <Route path="/features" element={<Features />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/services/imo-tank-container" element={<IMOTankContainerCertification />} />
          <Route path="/services/condition-survey" element={<ConditionSurveyImportExport />} />
          <Route path="/services/on-off-hire-survey" element={<OnOffHireSurvey />} />
          <Route path="/services/pre-post-repair-survey" element={<PrePostRepairSurvey />} />
          <Route path="/services/tank-inspection-report" element={<TankInspectionReport />} />

          {/* Authentication Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Admin Dashboard */}
          <Route
            path="/admin-dashboard"
            element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AdminDashboard />} />}
          />

          {/* Documentation Manager Dashboard */}
          <Route
            path="/documentation-manager-dashboard"
            element={<PrivateRoute roles={['Documentation Manager']} element={<DocumentationManagerDashboard />} />}
          />

          {/* Survey Inspector Dashboard */}
          <Route
            path="/survey-inspector-dashboard"
            element={<PrivateRoute roles={['Survey Inspector']} element={<SurveyInspectorDashboard />} />}
          />

          {/* Client Dashboard */}
          <Route
            path="/client-dashboard"
            element={<PrivateRoute roles={['Client']} element={<ClientDashboard />} />}
          />

          {/* Accountant Dashboard */}
          <Route
            path="/accountant-dashboard"
            element={<PrivateRoute roles={['Accountant']} element={<AccountantDashboard />} />}
          />

          {/* User Management Routes */}
          <Route
            path="/users"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Accountant']} element={<UserList />} />}
          />
          <Route
            path="/users/add"
            element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddUser />} />}
          />
          <Route
            path="/users/edit/:userId"
            element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditUser />} />}
          />
          <Route
            path="/users/view/:userId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Accountant']} element={<ViewUser />} />}
          />
          
          {/* Tank Management Routes */}
          <Route
            path="/tanks"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Accountant']} element={<TankList />} />}
          />
          <Route
            path="/tanks/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddTank />} />}
          />
          <Route
            path="/tanks/edit/:tankId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditTank />} />}
          />
          <Route
            path="/tanks/view/:tankId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Accountant', 'Client']} element={<ViewTank />} />}
          />

          {/* Container Management Routes */}
          <Route
            path="/containers"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Accountant']} element={<ContainerList />} />}
          />
          <Route
            path="/containers/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddContainer />} />}
          />
          <Route
            path="/containers/edit/:containerId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditContainer />} />}
          />
          <Route
            path="/containers/view/:containerId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Accountant', 'Client']} element={<ViewContainer />} />}
          />

          {/* Role Management Routes */}
          <Route
            path="/roles"
            element={<PrivateRoute roles={['System Admin']} element={<RoleList />} />}
          />
          <Route
            path="/roles/add"
            element={<PrivateRoute roles={['System Admin']} element={<AddRole />} />}
          />
          <Route
            path="/roles/edit/:roleId"
            element={<PrivateRoute roles={['System Admin']} element={<EditRole />} />}
          />
          <Route
            path="/roles/view/:roleId"
            element={<PrivateRoute roles={['System Admin']} element={<ViewRole />} />}
          />

          {/* Contact Management Routes */}
          <Route
            path="/contact"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<ContactList />} />}
          />
          <Route
            path="/contact/view/:contactId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<ViewContact />} />}
          />
          <Route
            path="/contact/edit/:contactId"
            element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditContact />} />}
          />

          {/* Testimonial Management Routes */}
          <Route
            path="/testimonials"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<TestimonialList />} />}
          />
          <Route
            path="/testimonials/add"
            element={<PrivateRoute roles={['System Admin', 'Admin']} element={<AddTestimonial />} />}
          />
          <Route
            path="/testimonials/edit/:testimonialId"
            element={<PrivateRoute roles={['System Admin', 'Admin']} element={<EditTestimonial />} />}
          />
          <Route
            path="/testimonials/view/:testimonialId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<ViewTestimonial />} />}
          />

          {/* Tank Cleaning Management Routes */}
          <Route
            path="/tank-cleanings"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<TankCleaningList />} />}
          />
          <Route
            path="/tank-cleanings/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddTankCleaning />} />}
          />
          <Route
            path="/tank-cleanings/edit/:cleaningId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditTankCleaning />} />}
          />
          <Route
            path="/tank-cleanings/view/:cleaningId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<ViewTankCleaning />} />}
          />
          <Route
            path="/tank-cleanings/:cleaningId/files"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector','client', 'Accountant']} element={<TankCleaningFileCabinet />} />}
          />

          {/* TI Report Management Routes */}
          <Route
            path="/ti-reports"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<TIReportList />} />}
          />
          <Route
            path="/ti-reports/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddTIReport />} />}
          />
          <Route
            path="/ti-reports/edit/:tiReportId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditTIReport />} />}
          />
          <Route
            path="/ti-reports/view/:tiReportId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<ViewTIReport />} />}
          />
          <Route
            path="/ti-reports/:tiReportId/files"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<TIReportFileCabinet />} />}
          />

          {/* Dry Box Survey Management Routes */}
          <Route
            path="/dry-box-surveys"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<DryBoxSurveyList />} />}
          />
          <Route
            path="/dry-box-surveys/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddDryBoxSurvey />} />}
          />
          <Route
            path="/dry-box-surveys/edit/:surveyId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditDryBoxSurvey />} />}
          />
          <Route
            path="/dry-box-surveys/view/:surveyId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<ViewDryBoxSurvey />} />}
          />
          <Route
            path="/dry-box-surveys/:surveyId/files"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<DBSFileCabinet />} />}
          />

          {/* Location Management Routes */}
          <Route
            path="/locations"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager',]} element={<LocationList />} />}
          />
          <Route
            path="/locations/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager',]} element={<AddLocation />} />}
          />
          <Route
            path="/locations/edit/:locationId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager',]} element={<EditLocation />} />}
          />
          <Route
            path="/locations/view/:locationId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager',]} element={<ViewLocation />} />}
          />

          {/* OnHire Management Routes */}
          <Route 
            path="/onhires"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<OnHireList />} />} 
          />
          <Route 
            path="/onhires/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddOnHire />} />} 
          />
          <Route 
            path="/onhires/edit/:onhireId" 
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditOnHire />} />} 
          />
          <Route 
            path="/onhires/view/:onhireId" 
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<ViewOnHire />} />} 
          />

          {/* EI Receipt Management Routes */}
          <Route
            path="/ei-receipts"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<EIReceiptList />} />}
          />
          <Route
            path="/ei-receipts/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<AddEIReceipt />} />}
          />
          <Route
            path="/ei-receipts/edit/:receiptId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector']} element={<EditEIReceipt />} />}
          />
          <Route
            path="/ei-receipts/view/:receiptId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Documentation Manager', 'Survey Inspector', 'Client', 'Accountant']} element={<ViewEIReceipt />} />}
          />

          {/* Billing Management Routes */}
          <Route
            path="/billings"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<BillingList />} />}
          />
          <Route
            path="/billings/add"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<AddBilling />} />}
          />
          <Route
            path="/billings/edit/:billingId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<EditBilling />} />}
          />
          <Route
            path="/billings/view/:billingId"
            element={<PrivateRoute roles={['System Admin', 'Admin', 'Accountant']} element={<ViewBilling />} />}
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
